import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { archiveCompany, exportCompanies, getCompanies } from '../api/Company';
import { getAllUsers } from '../api/User';
import { ICompany } from '../util/DataModelInterface'
import { DataTable } from '../components/DataTable';
import { Spinner } from '../components/Spinner';
import { useNavigate } from 'react-router-dom';
import { Typeahead } from 'react-bootstrap-typeahead';
import { ConfirmModal } from '../components/ConfirmModal';
import useToastHandler from '../hooks/useToastHandler';

interface ICompanyTableData {
  // UI columns: name, bid, users, actions
  id: string;
  name: string;
  bid: string;
  state: string;
  users: number;
  actions: JSX.Element;
}


export const Companies = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { addToast } = useToastHandler();
    const [companies, setCompanies] = useState([] as ICompany[]);
    const [loading, setLoading] = useState(false);
    const [companyTableData, setCompanyTableData] = useState([] as ICompanyTableData[]);
    const [selections, setSelections] = useState([] as ICompany[]);
    const [confirmArchModalActive, setConfirmArchModalActive] = useState(false);
    const [confirmModalTitle, setConfirmModalTitle] = useState("");
    const [confirmModalContent, setConfirmModalContent] = useState("");
    const [selectedCompany, setSelectedCompany] = useState({} as ICompany);


    useEffect(() => {
      setLoading(true);

      const fetchUsers = () => getAllUsers();
      const fetchCompanies = () => update();

      Promise.all([fetchUsers(), fetchCompanies()])
        .finally(() => {
            setLoading(false);
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (companies.length > 0) {
        let selected = selections.length > 0 ? companies.filter(c => c.id === selections[0].id) : [];
        let compData = selections.length > 0 ? selected : companies;
        parseTableData(compData);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selections, companies]);

    const update = async () => {
      await getCompanies(true).then((data: any) => {
        if (data?.error) {
          addToast(data.error, 'companies', 'fetch');
        } else if (data) {
          // console.log("companies data", data);
          setCompanies(data.sort((a: { name: string; }, b: { name: string; }) => {
            const nameA = a.name.toUpperCase(); // ignore upper and lowercase
            const nameB = b.name.toUpperCase(); // ignore upper and lowercase
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          }));
          //parseTableData(data); // called from useEffect instead...
        }
      });
    }

    const confirmArchiveCompany = (company: ICompany) => {
      let changedComp = {...company} as ICompany;
      changedComp.archived = !company.archived;
      setSelectedCompany(changedComp);

      let title = changedComp.archived ? intl.formatMessage({id: 'confirm.archive'}) : intl.formatMessage({id: 'confirm.activate'});
      let textContent = changedComp.archived ? intl.formatMessage({id: 'confirm.archive.content'}) : intl.formatMessage({id: 'confirm.activate.content'});

      setConfirmModalTitle(title);
      setConfirmModalContent(textContent);
      setConfirmArchModalActive(true);
    }

    const companyActionButtons = (company: ICompany) => {
      let archived = company.archived;
      let icon = archived ? 'fa-check' : 'fa-archive';
      let text = archived ? 'button.activate' : 'button.archive';
      return (
        <>
          <button className="btn btn-secondary" title={intl.formatMessage({id: 'companies.modify'})}
            onClick={ () => { navigate('/companymanagement/company', { replace: false, state: { company }});}}>
            <i className="fa fa-edit me-2"></i>
            <span><FormattedMessage id={'companies.modify'}/></span>
          </button>
          <button className="btn btn-secondary" title={intl.formatMessage({id: text})}
            onClick={ () => confirmArchiveCompany(company) }>
            <i className={"fa me-2 " + icon}></i>
            <span><FormattedMessage id={text}/></span>
          </button>
        </>
      );
    }

    // const checkedIconAndText = () => <><i className="fa fa-check me-2" title="checkIcon"></i><FormattedMessage id="companies.active"/></>;
    // const arhcivedIconAndText = () => <><i className="fa fa-archive me-2" title="archiveIcon"></i><FormattedMessage id="companies.archived"/></>;

    const parseTableData = (companies: ICompany[]) => {
      let data = [] as ICompanyTableData[];
      // let compData = selections.length > 0 ? selections : companies;
      companies.forEach((c, i) => {
        data[i] = {
          id: c.id,
          name: c.name,
          bid: c.business_id,
          state: c.archived ? intl.formatMessage({id: 'companies.archived'}) : intl.formatMessage({id: 'companies.active'}),
          users: c.users?.length,
          actions: companyActionButtons(c)
        };
      });
      setCompanyTableData(data);
    }

    const exportComp = () => {
      let jsonData = [] as any;

      let selectedCompanies = companies;
      selectedCompanies.forEach((c: ICompany) => {
        let row = {} as any;
        row[intl.formatMessage({id: 'companies.name'})] = c.name;
        row[intl.formatMessage({id: 'companies.bid'})] = c.business_id;
        row[intl.formatMessage({id: 'companies.address'})] = c.address;
        row[intl.formatMessage({id: 'companies.postal_code'})] = c.postal_code;
        row[intl.formatMessage({id: 'companies.city'})] = c.city;
        row[intl.formatMessage({id: 'companies.country'})] = c.country;
        row[intl.formatMessage({id: 'companies.phone'})] = c.phone;
        row[intl.formatMessage({id: 'companies.www'})] = c.www;
        row[intl.formatMessage({id: 'companies.archived'})] = c.archived;
        row[intl.formatMessage({id: 'companies.archive_period'})] = c.archive_period;
        row[intl.formatMessage({id: 'companies.member'})] = c.member;
        row[intl.formatMessage({id: 'companies.member_since'})] = c.member_since;
        row[intl.formatMessage({id: 'companies.reporting_started'})] = c.reporting_started;
        row[intl.formatMessage({id: 'companies.reporting_ended'})] = c.reporting_ended;
        row[intl.formatMessage({id: 'companies.certificate'})] = c.certificate;
        jsonData.push(row);
      });

      exportCompanies(
      { header: intl.formatMessage({id: 'companies.export.header'}),
        data: jsonData
      }).then((data: any) => {
        if (data?.error) {
          addToast(data.error, 'companies', 'fetch');
        }
      })
    }

    const archCompany = async () => {
      let company = {
        name: selectedCompany.name,
        id: selectedCompany.id,
        archived: selectedCompany.archived
      } as ICompany

      setLoading(true);
      let resp = await archiveCompany(company);
      if (resp?.error) {
        addToast(resp.error, 'companies', 'archive');
      } else {
        await update();
      }
      setLoading(false);
    }

    const confirmContent = () => <div>{confirmModalContent}</div>;

    const foundItem = (sel: ICompany[]) => {
      let selItem = sel[0];
      console.log("found", selItem);
      setSelections(sel);
      // parseTableData(companies); // called from useEffect instead
    }

    const filterByFields = ['business_id', 'name'];

    const toolBar = () => {
      return(
      <div className="toolbar">
        <span className="item-actions ms-auto">
          <button key='toolbarbutton' className="btn btn btn-primary btn-reminder"
            disabled={companies.length === 0}
            onClick={() => exportComp() }>
            <i className="me-2 fa fa-download" aria-hidden="true"></i>
            <FormattedMessage id={'button.report.download'}/>
          </button>
        </span>
      </div>);
    }

    return (
      <>
        <Spinner active={loading} />
        <ConfirmModal
          active={confirmArchModalActive}
          modalId='arhciveCompanyModal'
          onClose={() => setConfirmArchModalActive(false)}
          title={confirmModalTitle}
          content={confirmContent()}
          confirmbutton={
            {
              name: confirmModalTitle,
              class: 'btn btn-primary',
              onClick: () => { archCompany(); setConfirmArchModalActive(false) }
            }
          }
          />
        <h1><FormattedMessage id="companies.title"/></h1>
        <p className="info"><i className="fa fa-info-circle me-2"></i><FormattedMessage id="companies.title.info"/></p>

        <div className="controls d-flex flex-md-row flex-column justify-content-md-end my-5 align-items-md-center">

          <div className="search-group me-md-auto">
            {/* <label className="form-control-label sr-only" htmlFor="search">Search:</label> */}
            <Typeahead
              id="typeahead-search-comp"
              inputProps={{
                'aria-label': intl.formatMessage({id: 'companies.search.text'}),
              }}
              clearButton
              filterBy={filterByFields}
              labelKey="name"
              onChange={(sel) => {foundItem(sel as ICompany[]);}}
              options={companies}
              placeholder={intl.formatMessage({id: 'companies.search.text'})}
              renderMenuItemChildren={(option) => (
                <div>
                  {(option as ICompany).name}
                  <div>
                    <small><FormattedMessage id="companies.bid"/>: {(option as ICompany).business_id}</small>
                  </div>
                </div>
              )}
            />
          </div>

          <button className="btn btn-primary"
            onClick={ () => { navigate('/companymanagement/company', { replace: false, state: {}});}}
          >
            <i className="fa fa-plus-circle mr-2"></i>
            <FormattedMessage id="companies.add"/>
          </button>
        </div>

        {companyTableData.length > 0 &&
          <DataTable
            tableheadings={['name', 'bid', 'id', 'state', 'users', 'actions']}
            tabledata={companyTableData} tableType='companies' tableId='companieslist'
            toolbar={toolBar()}
            optSortableColumns={['name', 'state']}
          />
        }
      </>
    );
};
